<template>
  <Transition name="modal">
    <div
      v-if="showModalMain"
      class="modal-mask"
      id="modalPromotionPopupEmail"
      @click.self="closeModal"
      @keydown.esc="closeModal"
      tabindex="-1"
      ref="root"
    >
      <div class="modal-container">
        <button class="close-btn" type="button" @click="closeModal" aria-label="Fechar">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>

        <div class="modal-body">
          <img
            v-if="modalImage"
            :src="modalImage"
            alt=""
            class="hero"
          />

          <div v-if="!sent" class="content-wrap">
            <div class="content" v-html="sanitizedHtml"></div>

            <form @submit.prevent="onSubmit" novalidate class="form">
              <input
                v-model="website"
                name="website"
                type="text"
                tabindex="-1"
                autocomplete="off"
                class="honeypot"
                aria-hidden="true"
              />

              <input
                v-model="email"
                type="email"
                required
                class="email-input"
                :class="{ 'has-error': errorMessage }"
                :placeholder="emailPlaceholder"
                :disabled="submitting"
                autocomplete="email"
              />

              <label class="consent">
                <input type="checkbox" v-model="consent" :disabled="submitting" class="consent-checkbox" />
                <span class="consent-box" aria-hidden="true">
                  <svg v-if="consent" viewBox="0 0 16 16" width="12" height="12">
                    <path d="M3 8.5L6.5 12L13 4.5" stroke="#fff" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
                <span class="consent-text">{{ consentText }}</span>
              </label>

              <button
                type="submit"
                class="cta"
                :style="ctaStyle"
                :disabled="submitting || !canSubmit"
              >
                <span v-if="!submitting">{{ ctaLabel }}</span>
                <span v-else>{{ sendingText }}</span>
              </button>

              <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
            </form>
          </div>

          <div v-else class="success">
            <svg width="56" height="56" viewBox="0 0 48 48" fill="none" aria-hidden="true">
              <circle cx="24" cy="24" r="22" stroke="#2fb170" stroke-width="3" />
              <path d="M14 24L21 31L34 18" stroke="#2fb170" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <p>{{ successText }}</p>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <Transition
    name="footer"
    v-if="footerImageName"
    id="modalPromoFooter"
  >
    <div v-if="showModalFooter" class="">
      <div class="modal-container-footer">
        <div class="modal-header-footer">
          <button class="close-black" @click="closeModalFooter()">
            <img
              src="https://api.belladasemana.com.br/storage/2015/ico_x_black-t.png"
              alt="Close"
              class="close-modal"
            />
          </button>
        </div>
        <div class="modal-body-footer">
          <img
            :src="imageFooter"
            alt="Promotional Banner"
            title="Promotional banner"
            :width="isMobileFn() ? 391 : 1230"
            :height="isMobileFn() ? 48 : 150"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { isMobile } from "@/shared/utils/mobile";
import { useCookies } from "vue3-cookies";
import { usePromotionPopupCapture } from "@/composables/usePromotionPopupCapture";
import { usePromotionAudience } from "@/composables/usePromotionAudience";

const route = useRoute();
const storePromotionPopup = promotionPopupStore();
const { canSeePromotion } = usePromotionAudience();
const { cookies } = useCookies();

// Alias so template callers avoid collisions with the outer imported name.
const isMobileFn = isMobile;

const props = defineProps({
  optionsPromotionPopup: {
    type: Object,
    required: true,
  },
});

const showModalMain = ref(false);
const showModalFooter = ref(false);

// Árbitro único da tela — ver stores/modalArbiter.ts.
const arbitro = modalArbiterStore();

// Folga sobre o timer: a reserva precisa sobreviver ao tique de render entre o
// setTimeout disparar e o showModal() pedir a tela de fato.
const MARGEM_RESERVA_MS = 1000;

const email = ref("");
const consent = ref(false);
const website = ref("");
const submitting = ref(false);
const sent = ref(false);
const errorMessage = ref("");
const root = ref<HTMLElement | null>(null);

const locale = computed(() => (route.params.language === "en" ? "en" : "pt-br"));
const isEn = computed(() => locale.value === "en");

const modalImage = computed(() => props.optionsPromotionPopup.modal?.image ?? "");
const modalHtml = computed(() => props.optionsPromotionPopup.modal?.html ?? "");
const emailPlaceholder = computed(
  () => props.optionsPromotionPopup.modal?.inputs?.email_placeholder || (isEn.value ? "Your e-mail" : "Seu e-mail")
);
const ctaLabel = computed(
  () => props.optionsPromotionPopup.modal?.cta?.label || (isEn.value ? "I want" : "Quero")
);
const ctaStyle = computed(() => ({
  backgroundColor: props.optionsPromotionPopup.modal?.cta?.bg_color || "#d6336c",
  color: props.optionsPromotionPopup.modal?.cta?.text_color || "#ffffff",
}));

const consentText = computed(() =>
  isEn.value
    ? "I agree to receive promotional emails and accept the privacy terms."
    : "Aceito receber e-mails promocionais e concordo com os termos de privacidade."
);
const sendingText = computed(() => (isEn.value ? "Sending…" : "Enviando…"));
const successText = computed(() =>
  isEn.value ? "Check your inbox!" : "Verifique sua caixa de entrada em instantes."
);

const canSubmit = computed(() => {
  if (!email.value) return false;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value);
  return emailOk && consent.value;
});

// Strip inline font-size/font-family from TinyMCE-generated HTML so the modal
// renders with its own typography regardless of what the admin chose.
const sanitizedHtml = computed(() => {
  const raw = modalHtml.value || "";
  return raw
    .replace(/font-size\s*:\s*[^;"']+;?/gi, "")
    .replace(/font-family\s*:\s*[^;"']+;?/gi, "")
    .replace(/line-height\s*:\s*[^;"']+;?/gi, "");
});

const footerImageName = computed(() =>
  isMobileFn()
    ? props.optionsPromotionPopup.side_banner
    : props.optionsPromotionPopup.footer_banner
);
const imageFooter = computed(() =>
  "https://api.belladasemana.com.br/storage/promotions/" + footerImageName.value
);

const { submit } = usePromotionPopupCapture();

async function onSubmit() {
  if (website.value) return;
  if (!canSubmit.value) {
    errorMessage.value = isEn.value
      ? "Please enter a valid e-mail and accept the terms."
      : "Informe um e-mail válido e aceite os termos.";
    return;
  }
  errorMessage.value = "";
  submitting.value = true;

  const normalizedEmail = email.value.trim().toLowerCase();
  email.value = normalizedEmail;

  const result = await submit({
    promotion_popup_id: props.optionsPromotionPopup.promotion_popup_id,
    email: normalizedEmail,
    locale: locale.value,
    consent: consent.value,
    website: website.value,
  });

  submitting.value = false;

  if (result.ok) {
    sent.value = true;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "select_promotion",
      ecommerce: {
        promotion_name: props.optionsPromotionPopup.name,
      },
    });
    setTimeout(() => {
      cookies.set("bellaPromoShow", "0", 1000 * 60 * 60 * 24);
      arbitro.close("modalPromotionPopupEmail");
      showModalMain.value = false;
    }, 3000);
  } else {
    errorMessage.value = result.message;
  }
}

function closeModal() {
  cookies.set("bellaPromoShow", "0", 1000 * 60 * 60 * 24);
  arbitro.close("modalPromotionPopupEmail");
  showModalMain.value = false;
}

function closeModalFooter() {
  cookies.set("bellaPromoShow", "2", 1000 * 60 * 60 * 24);
  arbitro.close("modalPromoFooter");
  showModalFooter.value = false;
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") closeModal();
}

/**
 * Guarda a vez na tela durante a espera do `banner_timer` — ver o gêmeo em
 * ModalPromotionPopup.vue. Aqui a peça principal é o próprio modal de e-mail,
 * que sempre existe; por isso `temPrincipal` é fixo em true.
 *
 * @param  esperaMs  quanto falta do timer
 */
function segurarVezDaPromocao(esperaMs: number) {
  if (!canSeePromotion(props.optionsPromotionPopup)) {
    return;
  }

  const vaga = vagaQueSeraPedida({
    temPrincipal: true,
    temRodape: Boolean(footerImageName.value),
    display: String(props.optionsPromotionPopup.display ?? ""),
    contagem: String(cookies.get("countAccessPromotion") ?? ""),
    estagio: cookies.get("bellaPromoShow"),
    rodapeDesligado: cookies.isKey("showFooterPromoBanner"),
  });

  if (vaga === null) {
    return;
  }

  arbitro.segurar(
    vaga === "principal" ? "modalPromotionPopupEmail" : "modalPromoFooter",
    esperaMs + MARGEM_RESERVA_MS
  );
}

function tryModalPromotion() {
  var bellaTimerPromotion = storePromotionPopup.bellaTimerPromotionPopup;
  if (!bellaTimerPromotion.first_time_date) {
    var daysAccess = 1000 * 60 * 60 * 24 * 7;
    if (!cookies.isKey("countAccessPromotion")) {
      cookies.set("countAccessPromotion", "1", daysAccess);
    } else {
      if (parseInt(cookies.get("countAccessPromotion")) < 4) {
        cookies.set(
          "countAccessPromotion",
          String(parseInt(cookies.get("countAccessPromotion")) + 1),
          daysAccess
        );
      }
    }
    storePromotionPopup.setPromotionParams({ first_time_date: new Date().getTime() });
    bellaTimerPromotion = storePromotionPopup.bellaTimerPromotionPopup;
  }
  var timerParam = props.optionsPromotionPopup.banner_timer;
  var timerPromo = parseInt(timerParam) * 1000;
  if (timerPromo > 0) {
    if (!cookies.isKey("showFooterPromoBanner")) {
      var currentTimePromo = new Date().getTime();
      var diffPromo = currentTimePromo - Number(bellaTimerPromotion.first_time_date);
      var timeOutPromo = timerPromo - diffPromo < 0 ? 0 : timerPromo - diffPromo;
      segurarVezDaPromocao(timeOutPromo);
      setTimeout(() => {
        if (canSeePromotion(props.optionsPromotionPopup)) {
          showModal();
        }
      }, timeOutPromo);
    }
  } else {
    if (canSeePromotion(props.optionsPromotionPopup)) {
      showModal();
    }
  }
}

function showModal() {
  if (cookies.get("bellaPromoShow") === "1") {
    if (props.optionsPromotionPopup.display != "") {
      var arrayDisplay = props.optionsPromotionPopup.display.toString().split(",");
      if (
        arrayDisplay.includes(cookies.get("countAccessPromotion").toString())
      ) {
        if (
          arbitro.tryOpen("modalPromotionPopupEmail")
        ) {
          showModalMain.value = true;
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "view_promotion",
            ecommerce: {
              promotion_name: props.optionsPromotionPopup.name,
            },
          });
        }
      }
    }
  }

  if (
    !cookies.isKey("showFooterPromoBanner") &&
    cookies.get("bellaPromoShow") === "0"
  ) {
    if (footerImageName.value) {
      if (props.optionsPromotionPopup.display != "") {
        var arrayDisplay = props.optionsPromotionPopup.display.toString().split(",");
        if (arrayDisplay.includes(cookies.get("countAccessPromotion").toString())) {
          if (
            arbitro.tryOpen("modalPromoFooter")
          ) {
            showModalFooter.value = true;
          }
        }
      }
    } else {
      cookies.set("showFooterPromoBanner", "0", 1000 * 60 * 60 * 7);
    }
  }

  if (showModalMain.value || showModalFooter.value) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "paywall",
      paywall: {
        acionador: "ModalPromotionPopup",
      },
    });
  }
}

watch(
  () => showModalMain.value,
  async (isOpen) => {
    if (isOpen) {
      errorMessage.value = "";
      sent.value = false;
      email.value = "";
      consent.value = false;
      website.value = "";
      await nextTick();
      root.value?.focus();
      window.addEventListener("keydown", onKeydown);
    } else {
      window.removeEventListener("keydown", onKeydown);
    }
  }
);

onBeforeMount(() => {
  tryModalPromotion();
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  inset: 0;
  background-color: rgba(15, 20, 30, 0.65);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  padding: 24px;
  overflow-y: auto;
  transition: opacity 0.3s ease;
  outline: none;
}

.modal-container {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 48px);
  margin: auto;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  transition: transform 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.92);
  color: #333;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background 0.15s ease, transform 0.15s ease;
}

.close-btn:hover { background: #fff; transform: scale(1.05); }

.modal-body { display: flex; flex-direction: column; align-items: center; text-align: center; overflow-y: auto; min-height: 0; }

.hero { width: 100%; max-height: 240px; height: auto; object-fit: cover; display: block; }

.content-wrap { width: 100%; padding: 32px 32px 36px; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 32px; }

.content { font-size: 16px; line-height: 1.6; color: #2b2b2b; width: 100%; }
.content :deep(*) { font-family: inherit !important; font-size: inherit !important; line-height: inherit !important; color: inherit !important; background: transparent !important; max-width: 100%; }
.content :deep(h1), .content :deep(h2) { font-size: 24px !important; line-height: 1.3 !important; margin: 0 0 10px !important; }
.content :deep(h3), .content :deep(h4), .content :deep(h5), .content :deep(h6) { font-size: 18px !important; line-height: 1.3 !important; margin: 0 0 8px !important; }
.content :deep(p) { margin: 0 0 8px !important; }
.content :deep(p:last-child), .content :deep(*:last-child) { margin-bottom: 0 !important; }
.content :deep(ul), .content :deep(ol) { margin: 0 0 8px !important; padding-left: 20px; }
.content :deep(img) { max-width: 100%; height: auto; }

.form { width: 100%; display: flex; flex-direction: column; align-items: stretch; gap: 22px; }

.honeypot { position: absolute; left: -9999px; top: -9999px; opacity: 0; height: 0; width: 0; }

.email-input {
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #dadfe4;
  border-radius: 4px;
  font-size: 13.5px;
  color: #1a1a1a;
  background: #fff;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  font-family: inherit;
}
.email-input::placeholder { color: #9aa0a6; }
.email-input:focus { outline: none; border-color: #777; box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06); }
.email-input.has-error { border-color: #d64545; }

.consent {
  display: flex; align-items: flex-start; gap: 10px; margin: 0;
  font-size: 12.5px; line-height: 1.45; color: #55606b; cursor: pointer; text-align: left;
}
.consent-checkbox { position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none; }
.consent-box {
  flex: 0 0 18px; width: 18px; height: 18px; margin-top: 1px;
  border: 1.5px solid #c7cdd3; border-radius: 4px; background: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease; box-sizing: border-box;
}
.consent:hover .consent-box { border-color: #9aa0a6; }
.consent-checkbox:checked + .consent-box { background: #b20000; border-color: #b20000; }
.consent-checkbox:focus-visible + .consent-box { box-shadow: 0 0 0 3px rgba(178, 0, 0, 0.25); }
.consent-text { flex: 1; }

.cta {
  display: block; width: 100%; padding: 14px; border: 0; border-radius: 4px;
  font-size: 15px; font-weight: 600; letter-spacing: 0.2px; cursor: pointer;
  transition: filter 0.15s ease, transform 0.05s ease; font-family: inherit;
}
.cta:hover:not(:disabled) { filter: brightness(0.92); }
.cta:active:not(:disabled) { transform: translateY(1px); }
.cta:disabled { opacity: 0.5; cursor: not-allowed; }

.error { color: #d64545; margin: 12px 0 0; font-size: 13px; text-align: center; }

.success { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 56px 24px; text-align: center; width: 100%; }
.success p { margin: 0; font-size: 15px; color: #2b2b2b; }

/* Footer banner reuses the same container look as the banner method modal. */
.modal-container-footer {
  z-index: 9999;
  padding: 0;
  background-color: #fff;
  border-radius: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  opacity: 0.7;
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translate(-50%);
}
.modal-header-footer { display: block; text-align: center; }
.modal-body-footer { margin: 0; padding: 0; }
.close-black { position: absolute; right: 0; top: 0; z-index: 1; }
.close-modal { width: 25px; height: 25px; margin: 3px; }
:deep(.modal-body-footer img) { width: 100%; }

@media screen and (max-width: 769px) {
  .modal-container-footer { width: 95%; }
  :deep(.modal-body-footer img) { width: 100%; height: auto; }
  .close-modal { width: 20px; height: 20px; margin: 3px; }
}
</style>
